import React from "react";
import { styled } from "@xstyled/styled-components";
import { css, keyframes } from "styled-components";
import { Tooltip as MuiTooltip } from "@mui/material";
import { tokens } from "../utils/theme";

// Define Keyframes
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const ContentHeader = styled.header`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(35, 40, 59, 0.6)' : 'rgba(15, 23, 42, 0.14)'};
  
  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const ContentTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Chip = styled.div`
  align-self: flex-start;
  padding: 3px 10px;
  font-size: 11px;
  letter-spacing: 0.08em;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.6)' : 'rgba(15,118,110,0.38)'};
  color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).blueAccent[500] : '#0b3b35'};
  text-transform: uppercase;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'linear-gradient(90deg, rgba(128, 203, 196, 0.15), transparent)'
    : 'linear-gradient(90deg, rgba(15,118,110,0.14), rgba(255,255,255,0))'};
  box-shadow: ${props => props.theme.palette.mode === 'dark' ? 'none' : '0 10px 24px rgba(15,118,110,0.10)'};
`;

export const Subtitle = styled.p`
  font-size: 13px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  font-style: italic;
  margin: 0;
`;

export const ContentActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
  margin-bottom: 4px;

  @media (max-width: 720px) {
    justify-content: flex-start;
  }
`;

export const StyledBtnIcon = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid ${props => props.primary
    ? (props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.7)' : 'rgba(130, 170, 255, 0.7)')
    : (props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).grey[500] : 'rgba(15, 23, 42, 0.14)')};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.95)' : 'rgba(255, 255, 255, 0.78)'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  cursor: pointer;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  transition: all 0.18s ease-out;
  
  /* Animation */
  ${props => props.$spinning && css`
    animation: ${spin} 1s linear infinite;
    pointer-events: none;
    opacity: 0.8;
    
    /* Disable hover transform when spinning to prevent conflict */
    &:hover {
      transform: none;
    }
  `}

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(32, 37, 58, 0.96)' : 'rgba(255, 255, 255, 0.92)'};
    color: ${props => tokens(props.theme.palette.mode).grey[100]};
    border-color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).primary[500] : 'rgba(15,118,110,0.35)'};
    box-shadow: 0 0 12px ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.35)' : 'rgba(15,118,110,0.18)'};
    
    /* Only apply hover transform if NOT spinning */
    ${props => !props.$spinning && css`
      transform: translateY(-1px);
    `}
  }
`;

export const BtnIcon = React.forwardRef(({ title, children, disabled, ...props }, ref) => {
  const btn = (
    <StyledBtnIcon ref={ref} disabled={disabled} {...props}>
      {children}
    </StyledBtnIcon>
  );

  if (title) {
    return (
      <MuiTooltip title={title} arrow enterDelay={150} leaveDelay={100}>
        <span style={{ display: "inline-flex" }}>{btn}</span>
      </MuiTooltip>
    );
  }
  return btn;
});

export const TabsNav = styled.nav`
  margin-top: 4px;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid rgba(35, 40, 59, 0.9);
  padding-bottom: 4px;
`;

export const Tab = styled.div`
  position: relative;
  padding: 6px 10px 8px;
  font-size: 12px;
  color: ${props => props.active ? tokens(props.theme.palette.mode).grey[100] : tokens(props.theme.palette.mode).grey[400]};
  cursor: pointer;
  border-radius: 8px 8px 0 0;
  transition: color 0.18s ease-out, background 0.18s ease-out;
  background: ${props => props.active
    ? (props.theme.palette.mode === 'dark' ? 'rgba(16, 20, 34, 0.98)' : 'rgba(255, 255, 255, 0.78)')
    : 'transparent'};

  &::after {
    content: "";
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: -4px;
    height: 2px;
    border-radius: 999px;
    background: linear-gradient(90deg, ${props => tokens(props.theme.palette.mode).primary[500]}, ${props => tokens(props.theme.palette.mode).blueAccent[500]});
    box-shadow: 0 0 12px ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.9)' : 'rgba(15,118,110,0.30)'};
    display: ${props => props.active ? 'block' : 'none'};
  }

  &:hover {
    color: ${props => tokens(props.theme.palette.mode).grey[100]};
    background: ${props => !props.active && (props.theme.palette.mode === 'dark' ? 'rgba(17, 21, 32, 0.9)' : 'rgba(15, 23, 42, 0.05)')};
  }
`;

export const Panel = styled.section`
  flex: 1;
  margin-top: 2px;
  background: ${props => props.theme.palette.mode === 'dark'
    ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.08), rgba(12, 14, 23, 0.98))'
    : 'radial-gradient(circle at top left, rgba(15,118,110,0.06), rgba(255,255,255,0.93))'};
  border-radius: 18px;
  border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
  padding: 12px 14px 12px;
  position: relative;

  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  min-width: 0;
  width: 100%;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.18)' : 'rgba(15,118,110,0.12)'};
    opacity: ${props => props.theme.palette.mode === 'dark' ? '0.3' : '0.25'};
  }
`;

export const PanelHeader = styled.div`
  position: relative;
  z-index: 20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

export const PanelTitle = styled.span`
  text-transform: uppercase;
  letter-spacing: 0.12em;
`;

export const PanelFilters = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  font-size: 11px;
`;

export const BadgePill = styled.span`
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(130, 170, 255, 0.6)' : 'rgba(15,118,110,0.32)'};
  color: ${props => props.theme.palette.mode === 'dark' ? tokens(props.theme.palette.mode).primary[500] : '#0f766e'};
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(11, 14, 28, 0.9)' : 'rgba(15,118,110,0.08)'};
`;

export const PanelSelect = styled.div`
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(19, 22, 35, 0.98)' : 'rgba(255, 255, 255, 0.78)'};
  border-radius: 999px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? '#252a3e' : tokens(props.theme.palette.mode).grey[500]};
  padding: 4px 8px;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};

  select {
    background: transparent;
    border: none;
    outline: none;
    color: inherit;
    font-size: 11px;
    font-family: inherit;

    option {
      background: ${props => props.theme.palette.mode === 'dark' ? '#131623' : '#ffffff'};
      color: ${props => props.theme.palette.mode === 'dark' ? '#b0bec5' : 'inherit'};
    }
  }
`;

export const TableWrapper = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 4px;
  background: ${props => props.theme.palette.mode === 'dark' ? '#050710' : 'rgba(255, 255, 255, 0.78)'};
  border-radius: 14px;
  border: 1px solid ${props => props.theme.palette.mode === 'dark' ? '#20253a' : tokens(props.theme.palette.mode).grey[500]};
  overflow: auto;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  margin-right: 18px; /* Space for vertical scrollbar */
  margin-bottom: 18px; /* Space for horizontal scrollbar */
  
  /* Hide native scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none; /* Firefox */
`;

export const ScrollRail = styled.div`
  position: absolute;
  left: 14px;
  right: 32px; /* Avoid overlap with vertical scrollbar */
  bottom: 6px;
  height: 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  /* padding: 2px; removed for absolute positioning */
  z-index: 2;
  cursor: pointer;

  /* Glow effect */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 20%, rgba(128, 203, 196, 0.12), transparent 70%),
                radial-gradient(circle at 80%, rgba(130, 170, 255, 0.10), transparent 70%);
    opacity: 0.7;
    pointer-events: none;
  }
`;

export const ScrollThumb = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 6px; /* Fits inside 8px rail */
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
  border: 1px solid rgba(128, 203, 196, 0.20);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
  width: ${props => props.width || '38%'};
  left: ${props => props.left || '0%'};
  /* transition: margin-left 0.1s ease-out; REMOVED */
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

export const ScrollRailVertical = styled.div`
  position: absolute;
  top: 50px; /* Below header */
  bottom: 32px; /* Avoid overlap with horizontal scrollbar */
  right: 4px;
  width: 8px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  /* padding: 2px; removed */
  z-index: 2;
  cursor: pointer;

  /* Glow effect */
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: radial-gradient(circle at 20%, rgba(128, 203, 196, 0.12), transparent 70%),
                radial-gradient(circle at 80%, rgba(130, 170, 255, 0.10), transparent 70%);
    opacity: 0.7;
    pointer-events: none;
  }
`;

export const ScrollThumbVertical = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 6px; /* Fits inside 8px rail */
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
  border: 1px solid rgba(128, 203, 196, 0.20);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
  height: ${props => props.height || '38%'};
  top: ${props => props.top || '0%'};
  /* transition: margin-top 0.1s ease-out; REMOVED */
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
`;

export const Thead = styled.thead`
  background: ${props => props.theme.palette.mode === 'dark' ? '#080c18' : 'rgba(255, 255, 255, 0.94)'};
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const Tbody = styled.tbody`
  /* No specific styles needed yet, but keeping it for consistency */
`;

export const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-weight: 500;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => tokens(props.theme.palette.mode).grey[400]};
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? '#20253a' : tokens(props.theme.palette.mode).grey[500]};
  white-space: nowrap;

  @media (max-width: 600px) {
    &:nth-child(6), &:nth-child(7) { display: none; }
  }
`;

export const Tr = styled.tr`
  border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(23, 28, 46, 0.7)' : 'rgba(15, 23, 42, 0.08)'};
  background: ${props => props.theme.palette.mode === 'dark' ? '#050710' : 'rgba(255, 255, 255, 0.72)'};
  transition: background 0.18s ease-out, transform 0.18s ease-out, box-shadow 0.18s ease-out;

  &:nth-child(even) { 
    background: ${props => props.theme.palette.mode === 'dark' ? '#060a13' : 'rgba(15, 23, 42, 0.03)'};
  }

  &:hover {
    background: ${props => props.theme.palette.mode === 'dark' ? '#08101d' : 'rgba(15,118,110,0.06)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.palette.mode === 'dark' ? '0 4px 16px rgba(0, 0, 0, 0.6)' : '0 8px 22px rgba(16, 24, 40, 0.12)'};
  }
`;

export const Td = styled.td`
  padding: 7px 10px;
  white-space: nowrap;
  color: ${props => props.theme.palette.mode === 'dark' ? '#cfd8dc' : '#1f2937'};

  @media (max-width: 600px) {
    &:nth-child(6), &:nth-child(7) { display: none; }
  }
`;

export const TdSymbol = styled.span`
  font-weight: 500;
  color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

export const TdSide = styled.span`
  color: ${props => props.type === 'LONG' ? tokens(props.theme.palette.mode).greenAccent[500] : tokens(props.theme.palette.mode).redAccent[500]};
`;

export const TdPnl = styled.span`
  color: ${props => props.positive ? tokens(props.theme.palette.mode).greenAccent[500] : tokens(props.theme.palette.mode).redAccent[500]};
`;

export const TdSmall = styled.span`
  font-size: 11px;
  color: ${props => props.theme.palette.mode === 'dark' ? '#90a4ae' : tokens(props.theme.palette.mode).grey[400]};
  white-space: pre-wrap;
`;

export const Btn = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.5)' : 'rgba(15,118,110,0.4)';
    if (props.danger) return props.theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.5)' : 'rgba(225, 29, 72, 0.4)';
    return props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(15, 23, 42, 0.12)';
  }};
  background: ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.1)' : 'rgba(15,118,110,0.08)';
    if (props.danger) return props.theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.1)' : 'rgba(225, 29, 72, 0.08)';
    return 'transparent';
  }};
  color: ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e';
    if (props.danger) return props.theme.palette.mode === 'dark' ? '#ef5350' : '#be123c';
    return props.theme.palette.mode === 'dark' ? '#b0bec5' : '#475569';
  }};
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.2)' : 'rgba(15,118,110,0.15)';
    if (props.danger) return props.theme.palette.mode === 'dark' ? 'rgba(239, 83, 80, 0.2)' : 'rgba(225, 29, 72, 0.15)';
    return props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.06)';
  }};
    border-color: ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? '#80cbc4' : '#0f766e';
    if (props.danger) return props.theme.palette.mode === 'dark' ? '#ef5350' : '#be123c';
    return props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.2)';
  }};
    color: ${props => {
    if (props.primary) return props.theme.palette.mode === 'dark' ? '#b2dfdb' : '#115e59';
    if (props.danger) return props.theme.palette.mode === 'dark' ? '#ffcdd2' : '#9f1239';
    return props.theme.palette.mode === 'dark' ? '#fff' : '#1e293b';
  }};
  }
`;

export const SectionHeaderRow = styled(Tr)`
  background: rgba(30, 33, 48, 0.8);
  border-bottom: 2px solid rgba(128, 203, 196, 0.2);
`;

export const SectionHeaderCell = styled(Td)`
  font-size: 14px;
  font-weight: 700;
  color: ${props => props.theme.palette.mode === 'dark' ? '#80cbc4' : tokens(props.theme.palette.mode).primary[600]};
  padding: 12px 16px;
  text-transform: uppercase;
  letter-spacing: 1px;
  background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(15, 17, 26, 0.95)' : 'rgba(255, 255, 255, 0.9)'};
`;
